import axios from 'axios';

const API_BASE_URL = 'https://hr-service-git-618063327581.asia-southeast2.run.app/api';

const authClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': '*/*'
  }
});

// Add a request interceptor to inject the token
authClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 and refresh tokens
authClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Don't intercept auth routes (login/refresh) to prevent infinite loops and false errors
    const isAuthRoute = originalRequest.url.includes('/auth/login') || originalRequest.url.includes('/auth/refresh');

    // If we have a 401, trying to refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
      originalRequest._retry = true;
      try {
        const result = await authService.refreshToken();
        if (result && result.access_token) {
          // Update the failed request with the new access token
          originalRequest.headers.Authorization = `Bearer ${result.access_token}`;
          return authClient(originalRequest);
        }
      } catch (err) {
        // Refresh token failed, clear everything and require login again
        authService.clearTokens();
        // Optionially redirect to login: window.location.href = '/';
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Login
  login: async (loginVar, password) => {
    try {
      const response = await authClient.post('/auth/login', {
        login: loginVar,
        password: password
      });
      // Extract tokens (handling cases where they are wrapped in a 'data' object)
      const responseData = response.data.data || response.data;
      if (responseData && responseData.accessToken) {
        localStorage.setItem('access_token', responseData.accessToken);
      }
      if (responseData && responseData.refreshToken) {
        localStorage.setItem('refresh_token', responseData.refreshToken);
      }
      return responseData;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get current user profile
  getMe: async () => {
    try {
      const response = await authClient.get('/auth/me');
      localStorage.setItem('user_profile', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Refresh token
  refreshToken: async () => {
    try {
      const refToken = localStorage.getItem('refresh_token');
      if (!refToken) throw new Error("No refresh token available");

      const response = await authClient.post('/auth/refresh', {
        refresh_token: refToken
      });
      
      const responseData = response.data.data || response.data;
      if (responseData && responseData.accessToken) {
        localStorage.setItem('access_token', responseData.accessToken);
      }
      return responseData;
    } catch (error) {
      // If refresh fails, clear tokens so user has to login again
      authService.clearTokens();
      throw error.response?.data || error;
    }
  },

  // Change Password
  changePassword: async (oldPassword, newPassword, confirmPassword) => {
    try {
      const response = await authClient.put('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update Profile Info
  updateProfile: async (data, isMultipart = false) => {
    try {
      const config = {};
      const response = await authClient.put('/profile', data, config);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Face Registration
  registerFace: async (photo) => {
    try {
      const response = await authClient.post('/profile/register-face', { foto: photo });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Digital Signature Passphrase
  getPassphraseStatus: async () => {
    try {
      const response = await authClient.get('/profile/passphrase-status');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  setPassphrase: async (passphrase, confirmPassphrase) => {
    try {
      const response = await authClient.post('/profile/set-passphrase', {
        passphrase,
        confirm_passphrase: confirmPassphrase
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  requestPassphraseReset: async () => {
    try {
      const response = await authClient.post('/profile/request-passphrase-reset');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Bank Info (Using general updateProfile endpoint as per legacy code)
  updateBankInfo: async (bankData) => {
    try {
      const response = await authClient.put('/profile', bankData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Notifications
  getNotifications: async () => {
    try {
      const response = await authClient.get('/notifications');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getUnreadCount: async () => {
    try {
      const response = await authClient.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  markNotifAsRead: async (id) => {
    try {
      const response = await authClient.put(`/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  markAllNotifsAsRead: async () => {
    try {
      const response = await authClient.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  executeNotifAction: async (id) => {
    try {
      const response = await authClient.post(`/notifications/${id}/execute`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Penalty Settings
  getPenaltyCompanies: async () => {
    try {
      const response = await authClient.get('/settings/penalty-companies');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getPenaltyRules: async (companyId, jenis) => {
    try {
      const response = await authClient.get('/settings/penalty-rules', {
        params: { company_id: companyId, jenis_pelanggaran: jenis }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  savePenaltyRule: async (id, data) => {
    try {
      const response = id 
        ? await authClient.put(`/settings/penalty-rules/${id}`, data)
        : await authClient.post('/settings/penalty-rules', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deletePenaltyRule: async (id, companyId) => {
    try {
      const response = await authClient.delete(`/settings/penalty-rules/${id}`, {
        data: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getPenaltyConfig: async (companyId) => {
    try {
      const response = await authClient.get(`/settings/penalty-config/${companyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  savePenaltyConfig: async (companyId, data) => {
    try {
      const response = await authClient.put(`/settings/penalty-config/${companyId}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getComponentTypes: async (companyId) => {
    try {
      const response = await authClient.get('/payroll/component-types', {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  createSalaryComponentType: async (data) => {
    try {
      const response = await authClient.post('/payroll/component-types', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updateSalaryComponentType: async (id, data) => {
    try {
      const response = await authClient.put(`/payroll/component-types/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteSalaryComponentType: async (id, companyId) => {
    try {
      const response = await authClient.delete(`/payroll/component-types/${id}`, {
        data: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  copyComponentsFromHolding: async (data) => {
    try {
      const response = await authClient.post('/payroll/component-types/copy-from-holding', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getSalaryComponentPolicyMy: async () => {
    try {
      const response = await authClient.get('/settings/salary-component-policy/my');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getSalaryComponentPolicy: async (companyId) => {
    try {
      const response = await authClient.get('/settings/salary-component-policy', {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updateSalaryComponentPolicy: async (data) => {
    try {
      const response = await authClient.put('/settings/salary-component-policy', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Payroll Periods
  getPayrollPeriods: async (companyId) => {
    try {
      const response = await authClient.get('/payroll/periods', {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  createPayrollPeriod: async (data) => {
    try {
      const response = await authClient.post('/payroll/periods', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updatePayrollPeriod: async (id, data) => {
    try {
      const response = await authClient.put(`/payroll/periods/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  runPayroll: async (id, companyId) => {
    try {
      const response = await authClient.post(`/payroll/periods/${id}/run`, { company_id: companyId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  lockPayroll: async (id, companyId) => {
    try {
      const response = await authClient.post(`/payroll/periods/${id}/lock`, { company_id: companyId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  payPayroll: async (id, companyId) => {
    try {
      const response = await authClient.post(`/payroll/periods/${id}/pay`, { company_id: companyId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getPayrollSlips: async (periodId, companyId) => {
    try {
      const response = await authClient.get('/payroll/slips', {
        params: { period_id: periodId, company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getPph21Data: async (periodId, companyId) => {
    try {
      const response = await authClient.get(`/payroll/periods/${periodId}/pph21`, {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updatePph21PerEmployee: async (periodId, data) => {
    try {
      const response = await authClient.put(`/payroll/periods/${periodId}/pph21/per-employee`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  exportPayrollExcel: async (periodId, companyId) => {
    try {
      const response = await authClient.get(`/payroll/periods/${periodId}/export-excel`, {
        params: { company_id: companyId },
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getSlipPdf: async (slipId, companyId) => {
    try {
      const response = await authClient.get(`/payroll/slips/${slipId}/pdf`, {
        params: { company_id: companyId },
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Employee Salary Enrollment
  getEmployeesForPayroll: async (companyId) => {
    try {
      const response = await authClient.get('/profile/employees', {
        params: { company_id: companyId, limit: 1000 }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getEmployeeSalaryComponents: async (employeeId, companyId) => {
    try {
      const response = await authClient.get(`/payroll/employee-salary/${employeeId}`, {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  saveEmployeeSalaryComponent: async (data) => {
    try {
      const response = await authClient.post('/payroll/employee-salary', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteEmployeeSalaryComponent: async (id, companyId) => {
    try {
      const response = await authClient.delete(`/payroll/employee-salary/${id}`, {
        data: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Attendance Settings (Lokasi)
  getAttendanceSettings: async (companyId) => {
    try {
      const response = await authClient.get('/settings/attendance-settings', {
        params: { company_id: companyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getSettingsCompanies: async () => {
    try {
      const response = await authClient.get('/settings/companies');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  saveAttendanceSetting: async (id, data) => {
    try {
      const response = id
        ? await authClient.put(`/settings/attendance-settings/${id}`, data)
        : await authClient.post('/settings/attendance-settings', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteAttendanceSetting: async (id) => {
    try {
      const response = await authClient.delete(`/settings/attendance-settings/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Logout
  logout: async () => {
    try {
      await authClient.post('/auth/logout');
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      authService.clearTokens();
    }
  },

  // Helper clear function
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_profile');
  }
};

export const api = authClient;
